import React, { useEffect, useRef } from "react";
import swal from "sweetalert";
import html2canvas from "html2canvas";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import "@tensorflow/tfjs";
import { t } from "i18next";
import { connect } from "react-redux";

import { handlePSYCheatings } from "../../../../store/psychometric/actions";

const Detection = ({
  isExam,
  exitFullScreen,
  disableAntiCheat,
  handlePSYCheatings,
  applicantSessionId,
  psychometricQuestions,
  isFullScreen,
  psychometricSession,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null); // Reference to the stream

  const canvasRef = useRef(null);
  const lastSwalTimestamp = useRef(0);
  const intervalId = useRef(null);
  const SWAL_COOLDOWN = 5000;

  // console.log(isFullScreen);
  // console.log(psychometricQuestions);

  const triggerSwal = async (title, message, type) => {
    // console.log(isExam);

    if (!!disableAntiCheat) return;

    if (!!isExam) {
      const element = document.getElementById("videoRecordingCapturer");
      const canvas = await html2canvas(element);
      const image = canvas.toDataURL("image/png");

      handlePSYCheatings(
        {
          applicantSessionId: psychometricSession?.id || null,
          eventType: "UNAUTHORIZED_ACTION",
          image: image.includes("data:image/png;base64,")
            ? image.split("data:image/png;base64,")[1]
            : null,
          comment: title,
        },
        exitFullScreen
      );
    }

    const now = Date.now();
    if (now - lastSwalTimestamp.current > SWAL_COOLDOWN) {
      swal(title, message, type);
      lastSwalTimestamp.current = now;
    }
  };

  const detectPersonInImage = async (image) => {
    if (!image) return;
    try {
      const imgElement = new Image();
      imgElement.src = image;
      await imgElement.decode();

      if (imgElement.naturalWidth === 0 || imgElement.naturalHeight === 0) {
        return;
      }

      const model = await cocoSsd.load();

      const predictions = await model.detect(imgElement);

      const personCount = predictions.filter(
        (pred) => pred.class === "person"
      ).length;

      if (personCount === 0) {
        triggerSwal(t("noDetected"), t("returnToExam"), "error");
      } else if (personCount > 1) {
        triggerSwal(t("moreThanOne"), t("multipleDetected"), "error");
      }
    } catch (error) {
      console.error("Error during person detection:", error);
    }
  };

  const captureImageHandler = async () => {
    const element = document.getElementById("videoRecordingCapturer");
    if (!element) return;
    try {
      const canvas = await html2canvas(element);
      const image = canvas.toDataURL("image/png");
      await detectPersonInImage(image);
    } catch (error) {
      console.error("Error capturing image:", error);
    }
  };

  const detectFrame = (video, model) => {
    if (
      video.readyState === 4 &&
      video.videoWidth > 0 &&
      video.videoHeight > 0
    ) {
      model.detect(video).then((predictions) => {
        if (canvasRef.current) {
          renderPredictions(predictions);
          requestAnimationFrame(() => detectFrame(video, model));
        }
      });
    }
  };

  const renderPredictions = (predictions) => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    const font = "16px sans-serif";
    ctx.font = font;
    ctx.textBaseline = "top";

    let multipleFaceCount = 0;
    let message = "";
    const now = Date.now();

    predictions.forEach((prediction) => {
      const [x, y, width, height] = prediction.bbox;
      ctx.strokeStyle = "#00FFFF";
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);

      const textWidth = ctx.measureText(prediction.class).width;
      const textHeight = parseInt(font, 10);
      ctx.fillStyle = "#00FFFF";
      ctx.fillRect(x, y, textWidth + 8, textHeight + 8);

      if (prediction.class === "person") {
        multipleFaceCount++;
      } else if (now - lastSwalTimestamp.current > SWAL_COOLDOWN) {
        if (prediction.class === "cell phone") {
          message = t("cellPhoneDetected");
          triggerSwal(t("cellPhoneDetected"), t("actionRecorded"), "error");
        } else if (prediction.class === "book") {
          message = t("unAuthorizedObject");
          triggerSwal(t("unAuthorizedObject"), t("actionRecorded"), "error");
        } else if (prediction.class === "laptop") {
          message = t("unAuthorizedObject");
          triggerSwal(t("unAuthorizedObject"), t("actionRecorded"), "error");
        } else {
          message = t("faceNotDetected");
          triggerSwal(t("faceNotDetected"), t("returnToExam"), "error");
        }
      }

      ctx.fillStyle = "#000000";
      ctx.fillText(prediction.class, x, y);
    });

    if (
      multipleFaceCount >= 2 &&
      now - lastSwalTimestamp.current > SWAL_COOLDOWN
    ) {
      message = t("unAuthorizedObject");
      triggerSwal(t("unAuthorizedObject"), t("onThe3rd"), "error");
    }

    const count_facedetect = predictions.filter(
      (prediction) => prediction.class === "person"
    ).length;
    sessionStorage.setItem("count_facedetect", count_facedetect);
  };

  const initializeCamera = async () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: "user", width: 300, height: 225 },
        });
        streamRef.current = stream;
        videoRef.current.srcObject = stream;

        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => resolve();
        });

        const model = await cocoSsd.load();
        detectFrame(videoRef.current, model);
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    initializeCamera();

    intervalId.current = setInterval(captureImageHandler, 10000);
    return () => {
      clearInterval(intervalId.current);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  return (
    <div>
      <video
        className="border rounded border-info web-cam"
        autoPlay
        playsInline
        muted
        ref={videoRef}
        width="250"
        id="videoRecordingCapturer"
        style={{ borderRadius: "5px" }}
      />
      <canvas
        className="web-cam"
        ref={canvasRef}
        width="250"
        height="200"
        style={{ borderRadius: "5px" }}
      />
    </div>
  );
};

const mapStateToProps = ({ psychometricQuestions, psychometricSession }) => ({
  psychometricQuestions,
  psychometricSession,
});

export default connect(mapStateToProps, {
  handlePSYCheatings,
})(Detection);
